import { usePostHog } from 'posthog-js/react';
import {
    type RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { type ChatInputHandle } from '@apps/web/components/phoebe_chat/chat_input';
import {
    getPreferredRecordingMimeType,
    isMicrophonePermissionError,
    MAX_RECORDING_DURATION_MS,
    transcribeAudioBlob,
    VOICE_INPUT_ERROR_MESSAGE,
    VOICE_INPUT_PERMISSION_ERROR_MESSAGE,
} from '@apps/web/components/shared/messages/voice_input_utils';
import { EVENTS } from '@apps/web/lib/events';
import { useMode } from '@apps/web/lib/mode_store';

type VoiceInputStopReason = 'manual' | 'max_duration';

type UseVoiceRecordingOptions = {
    readonly organizationId: string | null;
    readonly chatInputRef: RefObject<ChatInputHandle | null>;
    readonly isAgentBusy: boolean;
    readonly isLoading: boolean;
    readonly isCreating: boolean;
    readonly isInputBlockedByPendingApproval: boolean;
    readonly isCalloutMode: boolean;
    readonly selectedConversationId: string | null;
    readonly canCreateOutreachRunOnSubmit: boolean;
};

type UseVoiceRecordingReturn = {
    readonly voiceInputError: string | null;
    readonly setVoiceInputError: (value: string | null) => void;
    readonly canRecordAudio: boolean;
    readonly isRecording: boolean;
    readonly isTranscribing: boolean;
    readonly startRecording: () => Promise<void>;
    readonly stopRecording: () => void;
    // Submit flow reads/clears these; bumping the generation drops in-flight
    // transcriptions.
    readonly draftIncludesVoiceInputRef: RefObject<boolean>;
    readonly transcriptionGenerationRef: RefObject<number>;
};

export function useVoiceRecording({
    organizationId,
    chatInputRef,
    isAgentBusy,
    isLoading,
    isCreating,
    isInputBlockedByPendingApproval,
    isCalloutMode,
    selectedConversationId,
    canCreateOutreachRunOnSubmit,
}: UseVoiceRecordingOptions): UseVoiceRecordingReturn {
    const posthog = usePostHog();
    const modePreference = useMode();
    const voiceInputFlow = isCalloutMode ? 'callout_chat' : 'general_chat';

    const [voiceInputError, setVoiceInputError] = useState<string | null>(null);
    const [canRecordAudio, setCanRecordAudio] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Array<Blob>>([]);
    const recordingTimeoutRef = useRef<number | null>(null);
    const recordingStartedAtRef = useRef<number | null>(null);
    const recordingStopReasonRef = useRef<VoiceInputStopReason>('manual');
    const draftIncludesVoiceInputRef = useRef(false);
    const transcriptionGenerationRef = useRef(0);

    const clearRecordingTimeout = useCallback(() => {
        if (recordingTimeoutRef.current === null) {
            return;
        }

        window.clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
    }, []);

    const stopMediaStream = useCallback(() => {
        mediaStreamRef.current?.getTracks().forEach((track) => {
            track.stop();
        });
        mediaStreamRef.current = null;
    }, []);

    useEffect(() => {
        setCanRecordAudio(
            typeof MediaRecorder !== 'undefined' &&
                typeof navigator !== 'undefined' &&
                Boolean(navigator.mediaDevices?.getUserMedia),
        );
    }, []);

    useEffect(() => {
        return () => {
            clearRecordingTimeout();

            const recorder = mediaRecorderRef.current;
            if (recorder && recorder.state !== 'inactive') {
                recorder.ondataavailable = null;
                recorder.onstop = null;
                recorder.stop();
            }

            stopMediaStream();
        };
    }, [clearRecordingTimeout, stopMediaStream]);

    const handleRecordingStop = useCallback(
        async (mimeType: string) => {
            setIsRecording(false);
            if (!organizationId) {
                clearRecordingTimeout();
                mediaRecorderRef.current = null;
                audioChunksRef.current = [];
                stopMediaStream();
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                return;
            }

            clearRecordingTimeout();
            mediaRecorderRef.current = null;

            const draftLength =
                chatInputRef.current?.getSerializedContent().trim().length ?? 0;
            const stopReason = recordingStopReasonRef.current;
            const recordingDurationMs =
                recordingStartedAtRef.current === null
                    ? undefined
                    : Math.round(
                          performance.now() - recordingStartedAtRef.current,
                      );
            recordingStartedAtRef.current = null;
            const audioChunks = [...audioChunksRef.current];
            audioChunksRef.current = [];
            stopMediaStream();
            const audioBlob = new Blob(audioChunks, { type: mimeType });

            posthog?.capture(EVENTS.VOICE_INPUT_RECORDING_STOPPED, {
                audio_size_bytes: audioBlob.size,
                composer: 'phoebe_chat_view',
                conversation_id: selectedConversationId ?? undefined,
                draft_length: draftLength,
                has_existing_draft: draftLength > 0,
                mode: modePreference,
                organization_id: organizationId,
                recording_duration_ms: recordingDurationMs,
                stop_reason: stopReason,
                voice_input_flow: voiceInputFlow,
            });

            if (!audioChunks.length) {
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                    audio_size_bytes: audioBlob.size,
                    composer: 'phoebe_chat_view',
                    conversation_id: selectedConversationId ?? undefined,
                    draft_length: draftLength,
                    has_existing_draft: draftLength > 0,
                    mode: modePreference,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    stage: 'empty_audio',
                    stop_reason: stopReason,
                    voice_input_flow: voiceInputFlow,
                });
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                return;
            }

            setIsTranscribing(true);
            const generation = transcriptionGenerationRef.current;
            try {
                const transcript = await transcribeAudioBlob({
                    audioBlob,
                    mode: modePreference,
                    organizationId,
                });
                if (generation !== transcriptionGenerationRef.current) {
                    return;
                }
                const trimmedTranscript = transcript.trim();
                if (!trimmedTranscript) {
                    posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                        audio_size_bytes: audioBlob.size,
                        composer: 'phoebe_chat_view',
                        conversation_id: selectedConversationId ?? undefined,
                        draft_length: draftLength,
                        has_existing_draft: draftLength > 0,
                        mode: modePreference,
                        organization_id: organizationId,
                        recording_duration_ms: recordingDurationMs,
                        stage: 'empty_transcript',
                        stop_reason: stopReason,
                        voice_input_flow: voiceInputFlow,
                    });
                    setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
                    return;
                }

                draftIncludesVoiceInputRef.current = true;
                setVoiceInputError(null);
                chatInputRef.current?.appendText(transcript);
                const afterAppendLength =
                    chatInputRef.current?.getSerializedContent().trim()
                        .length ?? 0;
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_SUCCEEDED, {
                    appended_to_existing_draft: draftLength > 0,
                    audio_size_bytes: audioBlob.size,
                    composer: 'phoebe_chat_view',
                    conversation_id: selectedConversationId ?? undefined,
                    draft_length: draftLength,
                    draft_length_after_append: afterAppendLength,
                    mode: modePreference,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    stop_reason: stopReason,
                    transcript_length: trimmedTranscript.length,
                    transcript_word_count:
                        trimmedTranscript.split(/\s+/).length,
                    voice_input_flow: voiceInputFlow,
                });
                window.requestAnimationFrame(() => {
                    chatInputRef.current?.focus();
                });
            } catch (error) {
                console.error('Failed to transcribe audio:', error);
                posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                    audio_size_bytes: audioBlob.size,
                    composer: 'phoebe_chat_view',
                    conversation_id: selectedConversationId ?? undefined,
                    draft_length: draftLength,
                    error_message:
                        error instanceof Error ? error.message : undefined,
                    has_existing_draft: draftLength > 0,
                    mode: modePreference,
                    organization_id: organizationId,
                    recording_duration_ms: recordingDurationMs,
                    stage: 'transcription_request',
                    stop_reason: stopReason,
                    voice_input_flow: voiceInputFlow,
                });
                setVoiceInputError(VOICE_INPUT_ERROR_MESSAGE);
            } finally {
                setIsTranscribing(false);
            }
        },
        [
            clearRecordingTimeout,
            chatInputRef,
            modePreference,
            organizationId,
            posthog,
            selectedConversationId,
            stopMediaStream,
            voiceInputFlow,
        ],
    );

    const startRecording = useCallback(async () => {
        if (
            !organizationId ||
            !canRecordAudio ||
            isRecording ||
            isTranscribing ||
            isAgentBusy ||
            isLoading ||
            isCreating ||
            isInputBlockedByPendingApproval ||
            (isCalloutMode &&
                !selectedConversationId &&
                !canCreateOutreachRunOnSubmit)
        ) {
            return;
        }

        const draftLength =
            chatInputRef.current?.getSerializedContent().trim().length ?? 0;
        setVoiceInputError(null);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            mediaStreamRef.current = stream;
            const mimeType = getPreferredRecordingMimeType();
            const recorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);
            const recorderMimeType =
                recorder.mimeType || mimeType || 'audio/webm';

            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            recorder.onstop = () => {
                void handleRecordingStop(recorderMimeType);
            };
            recorder.start();

            recordingStartedAtRef.current = performance.now();
            recordingStopReasonRef.current = 'manual';
            setIsRecording(true);
            posthog?.capture(EVENTS.VOICE_INPUT_RECORDING_STARTED, {
                composer: 'phoebe_chat_view',
                conversation_id: selectedConversationId ?? undefined,
                draft_length: draftLength,
                has_existing_draft: draftLength > 0,
                mode: modePreference,
                organization_id: organizationId,
                voice_input_flow: voiceInputFlow,
            });
            recordingTimeoutRef.current = window.setTimeout(() => {
                if (recorder.state !== 'recording') {
                    return;
                }

                recordingStopReasonRef.current = 'max_duration';
                recorder.stop();
            }, MAX_RECORDING_DURATION_MS);
        } catch (error) {
            console.error('Failed to start recording:', error);
            const permissionDenied = isMicrophonePermissionError(error);
            posthog?.capture(EVENTS.VOICE_INPUT_TRANSCRIPTION_FAILED, {
                composer: 'phoebe_chat_view',
                conversation_id: selectedConversationId ?? undefined,
                draft_length: draftLength,
                error_message:
                    error instanceof Error ? error.message : undefined,
                has_existing_draft: draftLength > 0,
                mode: modePreference,
                organization_id: organizationId,
                stage: permissionDenied
                    ? 'permission_denied'
                    : 'recording_start',
                voice_input_flow: voiceInputFlow,
            });
            setVoiceInputError(
                permissionDenied
                    ? VOICE_INPUT_PERMISSION_ERROR_MESSAGE
                    : VOICE_INPUT_ERROR_MESSAGE,
            );
            stopMediaStream();
            mediaRecorderRef.current = null;
        }
    }, [
        canRecordAudio,
        canCreateOutreachRunOnSubmit,
        chatInputRef,
        handleRecordingStop,
        isAgentBusy,
        isCalloutMode,
        isCreating,
        isInputBlockedByPendingApproval,
        isLoading,
        isRecording,
        isTranscribing,
        modePreference,
        organizationId,
        posthog,
        selectedConversationId,
        stopMediaStream,
        voiceInputFlow,
    ]);

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state !== 'recording') {
            return;
        }

        recordingStopReasonRef.current = 'manual';
        recorder.stop();
    }, []);

    return {
        voiceInputError,
        setVoiceInputError,
        canRecordAudio,
        isRecording,
        isTranscribing,
        startRecording,
        stopRecording,
        draftIncludesVoiceInputRef,
        transcriptionGenerationRef,
    };
}
